import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import i18n, { currentIntlLocale } from "@/i18n/config";
import { hasOwnSizes, renderImageUrl, type ImageFocal, cinemaSlug as sharedCinemaSlug, cityForName, madridToday } from "@/seo/shared";

export { movieHref } from "@/seo/shared";

type Tables = Database["public"]["Tables"];

/* -------------------------------------------------------------------- */
/*  Shared shapes                                                        */
/* -------------------------------------------------------------------- */

export type MovieListItem = Pick<
  Tables["movies"]["Row"],
  | "id"
  | "slug"
  | "title"
  | "synopsis"
  | "duration_minutes"
  | "language"
  | "subtitles"
  | "tags"
  | "curation_score"
  | "refs"
  | "year"
  | "image_focal_x"
  | "image_focal_y"
  | "og_image_url"
  | "image_width"
  | "image_height"
> & {
  movie_images: Pick<
    Tables["movie_images"]["Row"],
    "image_url" | "is_cover" | "display_order" | "has_sizes"
  >[];
};

export type Cinema = Tables["cinemas"]["Row"];

export type ScreeningCard = Pick<
  Tables["screenings"]["Row"],
  "id" | "movie_id" | "cinema_id" | "screening_date" | "screening_time" | "version" | "subtitles" | "tags"
> & {
  movies: Pick<
    Tables["movies"]["Row"],
    | "slug"
    | "title"
    | "tags"
    | "synopsis"
    | "duration_minutes"
    | "language"
    | "subtitles"
    | "curation_score"
    | "image_focal_x"
    | "image_focal_y"
  > & {
    movie_images: Pick<
      Tables["movie_images"]["Row"],
      "image_url" | "is_cover" | "display_order" | "has_sizes"
    >[];
  } | null;
  cinemas: Pick<Tables["cinemas"]["Row"], "name" | "city" | "address"> | null;
};

/* -------------------------------------------------------------------- */
/*  Etiquetas informativas de pase (screening_tags)                       */
/* -------------------------------------------------------------------- */

type ScreeningTagRow = Pick<
  Tables["screening_tags"]["Row"],
  "tag" | "label_es" | "label_ca" | "label_en" | "sort_order"
>;

let screeningTagCatalog: Map<string, ScreeningTagRow> | null = null;
let screeningTagPromise: Promise<void> | null = null;

/** Carga el catálogo `screening_tags` una vez por sesión. Los fetchers de
 *  screenings lo esperan antes de resolver, así `screeningTagLabels` puede
 *  ser síncrono en el render. */
export function ensureScreeningTagCatalog(): Promise<void> {
  if (!screeningTagPromise) {
    // Promise.resolve: el builder de Supabase es PromiseLike, no Promise.
    screeningTagPromise = Promise.resolve(
      supabase
        .from("screening_tags")
        .select("tag, label_es, label_ca, label_en, sort_order")
        .eq("is_active", true)
        .order("sort_order"),
    ).then(({ data }) => {
      screeningTagCatalog = new Map((data ?? []).map((r) => [r.tag, r]));
    });
  }
  return screeningTagPromise;
}

/** Etiquetas visibles de un pase (contrato: la etiqueta sale de
 *  `screening_tags` por locale y se ordena por `sort_order`; claves
 *  desconocidas se ignoran). */
export function screeningTagLabels(tags: string[] | null | undefined): string[] {
  if (!tags?.length || !screeningTagCatalog) return [];
  const locale = currentIntlLocale();
  const field =
    locale.startsWith("ca") || locale.startsWith("va")
      ? ("label_ca" as const)
      : locale.startsWith("en")
        ? ("label_en" as const)
        : ("label_es" as const);
  return tags
    .map((k) => screeningTagCatalog?.get(k))
    .filter((r): r is ScreeningTagRow => !!r)
    .sort((a, b) => a.sort_order - b.sort_order)
    // Gallego y euskera no tienen columna en la tabla: van en los JSON de
    // idioma (screeningTags.*); si falta alguna, la de la tabla.
    .map((r) => i18n.t(`screeningTags.${r.tag}`, { defaultValue: r[field] }));
}

/* -------------------------------------------------------------------- */
/*  Queries                                                              */
/* -------------------------------------------------------------------- */

// Las fechas de la cartelera son días de MADRID, no del navegador (28-10-2026):
// el prerender y /api/catalog ya trabajan así, y la SPA agrupaba por la fecha
// local, con lo que alguien fuera de España veía los pases de hoy un día
// movidos. `madridToday` vive en src/seo/shared.ts, compartido con el prerender.

function todayISO(): string {
  return madridToday();
}

/**
 * Catálogo público servido desde el CDN. En producción, las lecturas de catálogo
 * (idénticas para todos los visitantes) pasan por `/api/catalog` (función Edge con
 * Cache-Control s-maxage) en vez de golpear PostgREST por visitante — el cuello de
 * escala en el plan free de Supabase. Si la función falla, o en dev (`npm run dev`,
 * sin serverless), se cae a Supabase directo. La función devuelve el MISMO shape que
 * la query directa; la normalización (normalizeCity, agregación de rails) se aplica
 * después, igual en ambos caminos.
 */
async function catalogFetch<T>(rail: string, direct: () => Promise<T>): Promise<T> {
  if (import.meta.env.PROD) {
    try {
      const res = await fetch(`/api/catalog?r=${rail}`);
      if (res.ok) return (await res.json()) as T;
    } catch {
      /* cae a Supabase directo */
    }
  }
  return direct();
}

export async function fetchMovieById(
  id: string
): Promise<MovieListItem | null> {
  const { data, error } = await supabase
    .from("movies")
    .select(
      "id, slug, title, synopsis, duration_minutes, language, subtitles, tags, curation_score, refs, year, image_focal_x, image_focal_y, og_image_url, image_width, image_height, movie_images(image_url, is_cover, display_order, has_sizes)"
    )
    .eq("id", id)
    .eq("status", "published")
    .maybeSingle();

  if (error) throw error;
  return (data as MovieListItem) ?? null;
}

export async function fetchMovieBySlug(
  slug: string
): Promise<MovieListItem | null> {
  const { data, error } = await supabase
    .from("movies")
    .select(
      "id, slug, title, synopsis, duration_minutes, language, subtitles, tags, curation_score, refs, year, image_focal_x, image_focal_y, og_image_url, image_width, image_height, movie_images(image_url, is_cover, display_order, has_sizes)"
    )
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();

  if (error) throw error;
  return (data as MovieListItem) ?? null;
}

/** Slug nuevo de una ficha unida a otra (movie_slug_redirects, 31-10-2026), o
 *  null. El 301 lo da api/movie-redirect.ts; esto cubre la navegación dentro
 *  de la SPA y el servidor de desarrollo. */
export async function fetchSlugRedirect(oldSlug: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("movie_slug_redirects")
    .select("movies(slug)")
    .eq("old_slug", oldSlug)
    .maybeSingle();
  if (error) throw error;
  return (data?.movies as { slug: string | null } | null)?.slug ?? null;
}

/**
 * Sinopsis traducidas de todas las películas publicadas en un idioma. La tabla
 * `movie_translations` sólo guarda la sinopsis (el título es canónico); su RLS
 * ya limita las filas a películas publicadas. Devolvemos un mapa
 * `movie_id → synopsis` para superponerlo sobre la sinopsis en castellano
 * (fuente) que viaja embebida en el catálogo. Una sola query alimenta cartelera
 * + fichas de detalle en ese idioma; se cachea por idioma en React Query y ni
 * siquiera se dispara cuando el idioma activo es `es`.
 */
export async function fetchSynopsisTranslations(
  locale: string
): Promise<Record<string, string>> {
  if (locale === "es") return {};
  const { data, error } = await supabase
    .from("movie_translations")
    .select("movie_id, synopsis")
    .eq("locale", locale);

  if (error) throw error;
  const map: Record<string, string> = {};
  for (const row of (data ?? []) as { movie_id: string; synopsis: string | null }[]) {
    if (row.synopsis) map[row.movie_id] = row.synopsis;
  }
  return map;
}

/** Ciudades que se pliegan dentro de un área mayor para listado y filtros
 *  (alias en CITIES de src/seo/shared.ts): Gandía no se muestra como ciudad
 *  propia, su cine se lista bajo Valencia. */
export function normalizeCity(city: string | null): string | null {
  if (!city) return city;
  return cityForName(city)?.name ?? city;
}

/** Rewrite the nested cinema's city through {@link normalizeCity} so that
 *  grouping, filtering and display stay consistent across the whole app. */
function normalizeScreeningRows(rows: ScreeningCard[]): ScreeningCard[] {
  return rows.map((row) =>
    row.cinemas
      ? { ...row, cinemas: { ...row.cinemas, city: normalizeCity(row.cinemas.city) } }
      : row
  );
}

async function fetchCinemasDirect(): Promise<Cinema[]> {
  const { data, error } = await supabase
    .from("cinemas")
    .select("*")
    .order("name");

  if (error) throw error;
  return (data ?? []) as Cinema[];
}

export async function fetchCinemas(): Promise<Cinema[]> {
  const rows = await catalogFetch("cinemas", fetchCinemasDirect);
  return rows.map((c) => ({ ...c, city: normalizeCity(c.city) }));
}

/** Kebab-case slug from a cinema name. URL-safe, ASCII, lossless within
 *  the current catalogue: two cines distintos no comparten nombre (el
 *  prerender falla si dos cines visibles colisionan). */
export function cinemaSlug(name: string): string {
  return sharedCinemaSlug(name);
}

/** All upcoming screenings for one cinema, ordered by date+time. */
export async function fetchScreeningsForCinema(
  cinemaId: string
): Promise<ScreeningCard[]> {
  await ensureScreeningTagCatalog();
  const { data, error } = await supabase
    .from("screenings")
    .select(
      `id, movie_id, cinema_id, screening_date, screening_time, version, subtitles, tags,
       movies(slug, title, tags, synopsis, duration_minutes, language, subtitles, curation_score, image_focal_x, image_focal_y, image_width, image_height, movie_images(image_url, is_cover, display_order, has_sizes)),
       cinemas(name, city, address)`
    )
    .eq("cinema_id", cinemaId)
    .gte("screening_date", todayISO())
    .order("screening_date", { ascending: true })
    .order("screening_time", { ascending: true });

  if (error) throw error;
  return normalizeScreeningRows((data ?? []) as ScreeningCard[]);
}

/** All upcoming screenings (safety cap 5000) for the programación timeline. */
async function fetchProgrammingDirect(): Promise<ScreeningCard[]> {
  await ensureScreeningTagCatalog();
  const { data, error } = await supabase
    .from("screenings")
    .select(
      `id, movie_id, cinema_id, screening_date, screening_time, version, subtitles, tags,
       movies(slug, title, tags, synopsis, duration_minutes, language, subtitles, curation_score, image_focal_x, image_focal_y, image_width, image_height, movie_images(image_url, is_cover, display_order, has_sizes)),
       cinemas(name, city, address)`
    )
    .gte("screening_date", todayISO())
    .order("screening_date", { ascending: true })
    .order("screening_time", { ascending: true })
    .limit(5000);

  if (error) throw error;
  return (data ?? []) as ScreeningCard[];
}

export async function fetchProgramming(): Promise<ScreeningCard[]> {
  await ensureScreeningTagCatalog();
  const rows = await catalogFetch("programming", fetchProgrammingDirect);
  return normalizeScreeningRows(rows);
}

/** Versión del pase como clave de i18n (28-10-2026). Antes la ficha solo
 *  distinguía VO/doblada y se perdía el idioma de los subtítulos: con los
 *  subtítulos sale VOSE, VOSC o VOSI. Pásale los DEL PASE (`screenings.subtitles`,
 *  31-10-2026) y, si no constan, los de la peli: con los de la peli, Blue Heron
 *  salía VOSC en Babel porque se dio de alta desde Zumzeig. */
export function versionLabelKey(
  version: string | null | undefined,
  subtitles: string | null | undefined,
  language?: string | null,
): string | null {
  if ((version ?? "").toLowerCase() === "doblada") {
    // Una película en castellano no está doblada: el scraper marca «doblada»
    // todo pase en castellano (30-10-2026, «La Bola Negra» salía con DOB).
    return /espa|castell/i.test(language ?? "") ? null : "common.dub";
  }
  const sub = (subtitles ?? "").toLowerCase();
  if (!sub) return "common.vo";
  if (/espa|castell/.test(sub)) return "common.vose";
  if (/catal|valenci/.test(sub)) return "common.vosc";
  if (/ingl|engl/.test(sub)) return "common.vosi";
  return "common.vos";
}

/** Primer pase de cada película por ciudad, solo los de `since` en adelante
 *  (vista movie_city_first_screening, 31-10-2026). Clave «movie_id|ciudad».
 *  Sirve para saber qué es estreno: la web solo carga pases futuros. */
export async function fetchRecentFirstScreenings(since: string): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from("movie_city_first_screening")
    .select("movie_id, city, first_date")
    .gte("first_date", since);
  if (error) throw error;
  const out = new Map<string, string>();
  for (const r of data ?? []) if (r.movie_id && r.city && r.first_date) out.set(`${r.movie_id}|${r.city}`, r.first_date);
  return out;
}

/** Enlaces de entradas de un cine (movie_id → url), para la ficha del cine. */
export async function fetchMovieCinemasForCinema(cinemaId: string): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from("movie_cinemas")
    .select("movie_id, url")
    .eq("cinema_id", cinemaId);
  if (error) throw error;
  return new Map((data ?? []).filter((r) => r.url).map((r) => [r.movie_id, r.url as string]));
}

export type MovieCinemaLink = {
  cinema_id: string;
  url: string | null;
};

/** Per-movie cinema URLs (ticketing page per cinema that plays the movie). */
export async function fetchMovieCinemas(
  movieId: string
): Promise<MovieCinemaLink[]> {
  const { data, error } = await supabase
    .from("movie_cinemas")
    .select("cinema_id, url")
    .eq("movie_id", movieId);

  if (error) throw error;
  return (data ?? []) as MovieCinemaLink[];
}

/** Screenings for a given movie, future only. */
export async function fetchScreeningsForMovie(
  movieId: string
): Promise<ScreeningCard[]> {
  await ensureScreeningTagCatalog();
  const { data, error } = await supabase
    .from("screenings")
    .select(
      // Sin `movies(...)` (31-10-2026): son todos pases de la misma película,
      // que la ficha ya tiene; repetir sinopsis e imágenes en cada pase eran
      // ~100 KB en una película con 95 pases.
      `id, movie_id, cinema_id, screening_date, screening_time, version, subtitles, tags,
       cinemas(name, city, address)`
    )
    .eq("movie_id", movieId)
    .gte("screening_date", todayISO())
    .order("screening_date", { ascending: true })
    .order("screening_time", { ascending: true });

  if (error) throw error;
  return normalizeScreeningRows((data ?? []).map((r) => ({ ...r, movies: null })) as ScreeningCard[]);
}

/* -------------------------------------------------------------------- */
/*  Editorial rails (landing)                                            */
/* -------------------------------------------------------------------- */

/** Closed set of 14 editorial tags, in display order. Flat list, no
 *  subcategories — each becomes one rail on the landing. The labels match
 *  exactly what the categorizer wrote into `movies.editorial_tags`. */
export const EDITORIAL_RAIL_LABELS = [
  "Una lloradita y a seguir",
  "Cine de domingo",
  "Movidas de familia",
  "La vida, ains",
  "Más solo que la una",
  "Tierra de por medio",
  "Marrón existencial",
  "Joyas ocultas",
  "Las que dices que has visto",
  "¿Qué acabo de ver?",
  "Historias reales",
  "Las de dibujos",
  "Unas risas",
  "Pegado a la butaca",
] as const;

/** Stable i18n key per editorial label (`rails.tags.<key>`). The labels
 *  themselves stay in Spanish because they are the values stored in the DB. */
export const EDITORIAL_RAIL_KEYS: Record<(typeof EDITORIAL_RAIL_LABELS)[number], string> = {
  "Una lloradita y a seguir": "cry",
  "Cine de domingo": "sunday",
  "Movidas de familia": "family",
  "La vida, ains": "life",
  "Más solo que la una": "alone",
  "Tierra de por medio": "away",
  "Marrón existencial": "existential",
  "Joyas ocultas": "gems",
  "Las que dices que has visto": "classics",
  "¿Qué acabo de ver?": "weird",
  "Historias reales": "true",
  "Las de dibujos": "animated",
  "Unas risas": "laughs",
  "Pegado a la butaca": "thrill",
};

/** Hide rails thinner than this so the landing never shows a sad 2-poster row. */
const MIN_RAIL_MOVIES = 6;
/** Con filtro de ciudad el catálogo se parte (~BCN/VLC): el mínimo de 6 dejaría
 *  ciudades con muy pocos rails, así que se relaja un punto. */
const MIN_RAIL_MOVIES_CITY = 4;
/** Cap posters per rail to keep the payload + DOM reasonable. */
const MAX_RAIL_MOVIES = 18;

export type RailMovie = Pick<
  Tables["movies"]["Row"],
  "id" | "title" | "curation_score"
  | "image_focal_x"
  | "image_focal_y"
  | "image_width"
  | "image_height"
> & {
  /** Puede faltar en respuestas antiguas cacheadas en el CDN. */
  slug?: string | null;
  /** Ciudad en la que enlazar la ficha: la elegida o la primera con pases. */
  city?: string | null;
  movie_images: Pick<
    Tables["movie_images"]["Row"],
    "image_url" | "is_cover" | "display_order" | "has_sizes"
  >[];
};

export type EditorialRail = { label: string; movies: RailMovie[] };

type RailScreeningEmbed = {
  screening_date: string;
  cinemas?: { city: string | null } | null;
};

type RailQueryRow = RailMovie & {
  editorial_tags: string[] | null;
  screenings?: RailScreeningEmbed[];
};

/**
 * Editorial rails for the landing, anchored to the live agenda: only published
 * movies that have at least one upcoming screening. A movie lands in a rail per
 * editorial tag it carries; rails come back in {@link EDITORIAL_RAIL_LABELS}
 * order, thin rails dropped, each rail capped + ordered by curation_score desc.
 * El embed de cinemas(city) permite filtrar los rails por la ciudad elegida.
 */
async function fetchEditorialRailsRaw(): Promise<RailQueryRow[]> {
  const { data, error } = await supabase
    .from("movies")
    .select(
      "id, slug, title, curation_score, editorial_tags, image_focal_x, image_focal_y, image_width, image_height, movie_images(image_url, is_cover, display_order, has_sizes), screenings!inner(screening_date, cinemas!inner(city))"
    )
    .eq("status", "published")
    .gte("screenings.screening_date", todayISO())
    .order("curation_score", { ascending: false, nullsFirst: false })
    .order("title", { ascending: true });

  if (error) throw error;
  return (data ?? []) as RailQueryRow[];
}

/** ¿La película tiene algún pase próximo en esa ciudad? Tolerante al shape
 *  antiguo cacheado en el CDN (screenings sin cinemas embebido → no filtra). */
function railRowInCity(row: RailQueryRow, city: string): boolean {
  const screenings = row.screenings;
  if (!Array.isArray(screenings) || screenings.length === 0) return true;
  const hasCityInfo = screenings.some((s) => s && typeof s === "object" && "cinemas" in s);
  if (!hasCityInfo) return true;
  return screenings.some(
    (s) => normalizeCity((s?.cinemas?.city ?? "").trim() || null) === city
  );
}

export async function fetchEditorialRails(city?: string | null): Promise<EditorialRail[]> {
  // `movies` is the top-level resource, so each qualifying film appears once —
  // its upcoming screenings nest under it and only serve to filter the inner join.
  // El payload es único para todas las ciudades (la caché del CDN no se fragmenta);
  // el filtro por ciudad se aplica aquí, en el cliente.
  const rows = await catalogFetch("rails", fetchEditorialRailsRaw);

  const byLabel = new Map<string, RailMovie[]>();
  for (const row of rows) {
    if (city && !railRowInCity(row, city)) continue;
    const firstCity =
      (row.screenings ?? [])
        .map((s) => normalizeCity((s?.cinemas?.city ?? "").trim() || null))
        .find((c) => !!cityForName(c)) ?? null;
    const movie: RailMovie = {
      id: row.id,
      slug: row.slug ?? null,
      city: city ?? firstCity,
      title: row.title,
      image_focal_x: row.image_focal_x ?? null,
      image_focal_y: row.image_focal_y ?? null,
      // Sin ellas se suponía 16:9 y un póster de 216 px pedía 2.112 de ancho.
      image_width: row.image_width ?? null,
      image_height: row.image_height ?? null,
      curation_score: row.curation_score,
      movie_images: row.movie_images,
    };
    for (const label of row.editorial_tags ?? []) {
      const bucket = byLabel.get(label) ?? [];
      if (bucket.length < MAX_RAIL_MOVIES) bucket.push(movie);
      byLabel.set(label, bucket);
    }
  }

  const minMovies = city ? MIN_RAIL_MOVIES_CITY : MIN_RAIL_MOVIES;
  return EDITORIAL_RAIL_LABELS.map((label) => ({
    label,
    movies: byLabel.get(label) ?? [],
  })).filter((rail) => rail.movies.length >= minMovies);
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                              */
/* -------------------------------------------------------------------- */

/** Miniatura servida por el redimensionador de Supabase. Los originales son
 *  pósters de hasta 840×1200 (alguno de varios MB) que el navegador decodifica
 *  enteros para pintarlos a 220 px, y vuelve a rasterizar en cada cambio de
 *  ancho: ahí se iba el tiempo de la cartelera al redimensionar la ventana.
 *  Solo se reescriben las URLs del bucket público; las externas (placeholders)
 *  se devuelven intactas. */
export function sizedImageUrl(
  url: string,
  opts: {
    width: number;
    height: number;
    resize?: "cover" | "contain";
    quality?: number;
    /** Punto de interés: con él, el recorte lo hace el navegador (renderImageUrl). */
    focal?: ImageFocal | null;
  }
): string {
  const MARKER = "/storage/v1/object/public/";
  if (!url.includes(MARKER)) return url;
  const { width, height, resize = "cover", quality = 70 } = opts;
  if (resize === "cover" || hasOwnSizes(url, opts.focal)) return renderImageUrl(url, width, height, opts.focal, quality);
  const base = url.replace(MARKER, "/storage/v1/render/image/public/");
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}width=${width}&height=${height}&resize=${resize}&quality=${quality}`;
}

/** Punto focal de la portada como object-position (28-10-2026). Lo fija el
 *  admin de RE:CINE; NULL = centro. Con él, la misma portada vale para la
 *  caja 2:3, el hero 16:9 y las tarjetas sin que se corte la cara. */
export type FocalSource = { image_focal_x?: number | null; image_focal_y?: number | null };
export function focalPosition(m: FocalSource | null | undefined): string {
  const x = m?.image_focal_x;
  const y = m?.image_focal_y;
  return x == null || y == null ? "50% 50%" : `${x}% ${y}%`;
}

/** srcset con el redimensionador para una caja de proporción alto/ancho `ratio`. */
export function imageSrcSet(url: string, widths: number[], ratio: number, focal?: ImageFocal | null): string | undefined {
  if (!url.includes("/storage/v1/object/public/")) return undefined;
  // El descriptor es el ancho de la CAJA: con punto de interés la foto servida
  // es más ancha, pero el navegador elige por la caja que tiene que cubrir.
  return widths.map((w) => `${sizedImageUrl(url, { width: w, height: Math.round(w * ratio), focal })} ${w}w`).join(", ");
}

/** Foto de una tarjeta vertical de carrusel, 2x como mucho (a 3x no se nota y
 *  pesa más del doble). Una foto apaisada en caja 2:3 pediría 1.400 px o más
 *  para enseñar una franja: se limita a 1.100, unas 1,4 veces la caja (5-11-2026). */
const CARD_MAX_WIDTH = 1100;
const capWidth = (url: string) =>
  url.replace(/width=(\d+)/g, (_, w) => `width=${Math.min(Number(w), CARD_MAX_WIDTH)}`);
export function cardImageProps(url: string, focal?: ImageFocal | null) {
  return {
    src: capWidth(sizedImageUrl(url, { width: 528, height: 792, focal })),
    srcSet: imageSrcSet(url, [264, 528], 1.5, focal)?.replace(/width=\d+/g, capWidth),
    sizes: "(max-width: 640px) 216px, (max-width: 768px) 240px, (max-width: 1024px) 264px, 299px",
  };
}

export function coverImageUrl(movie: Pick<MovieListItem, "movie_images">): string {
  const images = movie.movie_images ?? [];
  if (images.length === 0) return "https://placehold.co/480x720/1A1A3E/F5F5F5?text=RE%3ACINE";
  const cover = images.find((i) => i.is_cover);
  if (cover) return cover.image_url;
  return [...images].sort((a, b) => a.display_order - b.display_order)[0]
    .image_url;
}

/** Foto del carrusel y de las filas de la cartelera: la portada, la misma que
 *  la ficha (5-11-2026). Antes se prefería cualquier foto que no fuera portada,
 *  pensando en fotogramas, pero esas filas son carteles antiguos: la portada
 *  sustituida a mano o la de la película fusionada («Ruega por nosotras»
 *  volvía a enseñar el cartel de la sala en «No te las pierdas»). */
export function stillImageUrl(
  movie: Pick<MovieListItem, "movie_images"> | null | undefined
): string {
  const images = movie?.movie_images ?? [];
  if (images.length === 0)
    return "https://placehold.co/640x360/1A1A3E/F5F5F5?text=RE%3ACINE";
  return coverImageUrl({ movie_images: images });
}

/** Punto de interés para la foto de stillImageUrl (31-10-2026). El del admin
 *  vale solo para la portada: si la foto elegida es la portada, el suyo; si no,
 *  el centro. Nunca null: sin punto, el servidor recortaba la foto a la caja
 *  de escritorio (16:10) y el móvil (3:4) volvía a recortar el centro de ese
 *  recorte («Arriba Tutto» enseñaba la puerta en vez del militar). */
export function stillFocal(
  movie: (Pick<MovieListItem, "movie_images"> & ImageFocal) | null | undefined
): ImageFocal {
  const still = stillImageUrl(movie);
  const cover = (movie?.movie_images ?? []).find((i) => i.is_cover)?.image_url;
  if (still !== cover) return { image_focal_x: 50, image_focal_y: 50 };
  const own = movie?.image_focal_x != null && movie?.image_focal_y != null;
  // Con sus medidas, para que el redimensionador no suponga 16:9.
  return {
    image_focal_x: own ? movie!.image_focal_x : 50,
    image_focal_y: own ? movie!.image_focal_y : 50,
    image_width: movie?.image_width,
    image_height: movie?.image_height,
    movie_images: movie?.movie_images,
  };
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(currentIntlLocale(), { day: "numeric", month: "short" });
}

export function formatDateLong(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(currentIntlLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/** Hora de fin aproximada (inicio + duración) como «HH:MM»; null sin duración.
 *  Para encadenar sesiones (29-10-2026). */
export function endTime(start: string, minutes: number | null | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  const [h, m] = start.split(":").map(Number);
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function formatTime(timeStr: string): string {
  return timeStr.slice(0, 5);
}
